import {
  ActiveTrial,
  ColorCurveKind,
  ColorCurvePoint,
  ColorMeasurement,
  ColorMeasurementDetail,
  TrialType
} from "../types";
import {
  isDateLikeSearchTerm,
  matchesDateFilter,
  matchesDateSearch
} from "./dateTime";

export type TrialDurationLike = {
  id?: number;
  descriptive_name: string;
  trial_type: string;
  start_time: string;
  end_time?: string | null;
};

export type ColorFilters = {
  date: string | null;
  methodIds: string[];
  observerIds: string[];
  illuminantIds: string[];
  glassPropertyIds: string[];
  glassColors: string[];
  trials: Record<TrialType, string[]>;
};

export type ColorSortField =
  | "measured_at"
  | "measurement"
  | "method"
  | "glass_color"
  | "thickness_mean_mm"
  | "brightness_percent"
  | "fe2_fe_total_percent"
  | "fe2_fe3_ratio"
  | "chromaticity"
  | "dominant_wavelength_nm";

export type ColorSortState = {
  field: ColorSortField;
  direction: "asc" | "desc";
};

export type BeerLambertComparison = {
  measurementId: string;
  slope: number | null;
  intercept: number | null;
  r2: number | null;
  rmse: number | null;
  meanDeltaAbsorbancePerMm: number | null;
  maxDeltaAbsorbancePerMm: number | null;
  integratedDeltaAbsorbancePerMm: number | null;
  pointCount: number;
};

type SvgSeries = {
  label: string;
  points: Array<{ x: number; y: number }>;
  color: string;
  dashed?: boolean;
};

type ChromaticityPoint = {
  id: string;
  label: string;
  x: number;
  y: number;
  color: string;
};

type ColorLikeMeasurement = Pick<
  ColorMeasurement,
  "l_star" | "a_star" | "b_star" | "x_chromaticity" | "y_chromaticity"
>;

export const COLOR_CURVE_LABELS: Record<ColorCurveKind, string> = {
  measured_total_transmittance: "Measured T",
  internal_transmittance: "Internal T",
  normalized_internal_transmittance: "Normalized T",
  absorbance: "Absorbance",
  absorbance_per_mm: "Absorbance / mm",
  immersion_corrected_absorbance: "Immersion-corrected A",
  other: "Other"
};

export const COLOR_TREND_METRICS: Array<{
  key: keyof ColorMeasurement;
  label: string;
}> = [
  { key: "brightness_percent", label: "Brightness (%)" },
  { key: "dominant_wavelength_nm", label: "Dominant / Complementary Wavelength (nm)" },
  { key: "fe2_fe_total_percent", label: "Fe2+ / Fe Total (%)" },
  { key: "fe2_fe3_ratio", label: "Fe2+ / Fe3+ Ratio" },
  { key: "excitation_purity", label: "Excitation Purity" }
];

export const DEFAULT_COLOR_CURVE_KIND: ColorCurveKind = "absorbance_per_mm";

export const defaultColorFilters: ColorFilters = {
  date: null,
  methodIds: [],
  observerIds: [],
  illuminantIds: [],
  glassPropertyIds: [],
  glassColors: [],
  trials: {
    Glaschemie: [],
    Brennstoff: [],
    "Energieverhältnis": [],
    Formgebung: []
  }
};

export const CHROMATICITY_LOCUS = [
  { wavelength_nm: 380, x: 0.174112, y: 0.004964 },
  { wavelength_nm: 385, x: 0.174008, y: 0.004981 },
  { wavelength_nm: 390, x: 0.173801, y: 0.004915 },
  { wavelength_nm: 395, x: 0.17356, y: 0.004923 },
  { wavelength_nm: 400, x: 0.173337, y: 0.004797 },
  { wavelength_nm: 405, x: 0.173021, y: 0.004775 },
  { wavelength_nm: 410, x: 0.172577, y: 0.004799 },
  { wavelength_nm: 415, x: 0.172087, y: 0.004833 },
  { wavelength_nm: 420, x: 0.171407, y: 0.005102 },
  { wavelength_nm: 425, x: 0.170301, y: 0.005789 },
  { wavelength_nm: 430, x: 0.168878, y: 0.0069 },
  { wavelength_nm: 435, x: 0.166895, y: 0.008556 },
  { wavelength_nm: 440, x: 0.164412, y: 0.010858 },
  { wavelength_nm: 445, x: 0.161105, y: 0.013793 },
  { wavelength_nm: 450, x: 0.156641, y: 0.017705 },
  { wavelength_nm: 455, x: 0.150985, y: 0.02274 },
  { wavelength_nm: 460, x: 0.14396, y: 0.029703 },
  { wavelength_nm: 465, x: 0.135503, y: 0.039879 },
  { wavelength_nm: 470, x: 0.124118, y: 0.057803 },
  { wavelength_nm: 475, x: 0.109594, y: 0.086843 },
  { wavelength_nm: 480, x: 0.091294, y: 0.132702 },
  { wavelength_nm: 485, x: 0.068706, y: 0.200723 },
  { wavelength_nm: 490, x: 0.045391, y: 0.294976 },
  { wavelength_nm: 495, x: 0.02346, y: 0.412703 },
  { wavelength_nm: 500, x: 0.008168, y: 0.538423 },
  { wavelength_nm: 505, x: 0.003859, y: 0.654823 },
  { wavelength_nm: 510, x: 0.01387, y: 0.750186 },
  { wavelength_nm: 515, x: 0.038852, y: 0.812016 },
  { wavelength_nm: 520, x: 0.074302, y: 0.833803 },
  { wavelength_nm: 525, x: 0.114161, y: 0.826207 },
  { wavelength_nm: 530, x: 0.154722, y: 0.805864 },
  { wavelength_nm: 535, x: 0.192876, y: 0.781629 },
  { wavelength_nm: 540, x: 0.22962, y: 0.754329 },
  { wavelength_nm: 545, x: 0.265775, y: 0.724324 },
  { wavelength_nm: 550, x: 0.301604, y: 0.692308 },
  { wavelength_nm: 555, x: 0.337363, y: 0.658848 },
  { wavelength_nm: 560, x: 0.373102, y: 0.624451 },
  { wavelength_nm: 565, x: 0.408736, y: 0.589607 },
  { wavelength_nm: 570, x: 0.444062, y: 0.554714 },
  { wavelength_nm: 575, x: 0.478775, y: 0.520202 },
  { wavelength_nm: 580, x: 0.512486, y: 0.486591 },
  { wavelength_nm: 585, x: 0.544787, y: 0.454434 },
  { wavelength_nm: 590, x: 0.575151, y: 0.424232 },
  { wavelength_nm: 595, x: 0.602933, y: 0.396497 },
  { wavelength_nm: 600, x: 0.627037, y: 0.372491 },
  { wavelength_nm: 605, x: 0.648233, y: 0.351395 },
  { wavelength_nm: 610, x: 0.665764, y: 0.334011 },
  { wavelength_nm: 615, x: 0.680079, y: 0.319747 },
  { wavelength_nm: 620, x: 0.691504, y: 0.308342 },
  { wavelength_nm: 625, x: 0.700606, y: 0.299301 },
  { wavelength_nm: 630, x: 0.707918, y: 0.292027 },
  { wavelength_nm: 635, x: 0.714032, y: 0.285929 },
  { wavelength_nm: 640, x: 0.719033, y: 0.280935 },
  { wavelength_nm: 645, x: 0.723032, y: 0.276948 },
  { wavelength_nm: 650, x: 0.725992, y: 0.274008 },
  { wavelength_nm: 655, x: 0.728272, y: 0.271728 },
  { wavelength_nm: 660, x: 0.729969, y: 0.270031 },
  { wavelength_nm: 665, x: 0.731089, y: 0.268911 },
  { wavelength_nm: 670, x: 0.731993, y: 0.268007 },
  { wavelength_nm: 675, x: 0.732719, y: 0.267281 },
  { wavelength_nm: 680, x: 0.733417, y: 0.266583 },
  { wavelength_nm: 685, x: 0.734047, y: 0.265953 },
  { wavelength_nm: 690, x: 0.73439, y: 0.26561 },
  { wavelength_nm: 695, x: 0.734592, y: 0.265408 },
  { wavelength_nm: 700, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 705, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 710, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 715, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 720, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 725, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 730, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 735, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 740, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 745, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 750, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 755, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 760, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 765, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 770, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 775, x: 0.73469, y: 0.26531 },
  { wavelength_nm: 780, x: 0.73469, y: 0.26531 }
] as const;

export const CHROMATICITY_LABEL_POINTS = CHROMATICITY_LOCUS.filter((point) =>
  [380, 420, 460, 480, 500, 520, 540, 560, 580, 600, 620, 780].includes(point.wavelength_nm)
);

export const CHROMATICITY_BACKGROUND_IMAGE = buildChromaticityBackgroundImage();
export const LAB_COLOR_PLANE_IMAGE = buildLabColorPlaneImage();

export function deriveActiveTrials(
  rows: TrialDurationLike[],
  measuredAt: string | null | undefined
): ActiveTrial[] {
  if (!measuredAt) return [];
  const measuredTime = Date.parse(measuredAt);
  if (!Number.isFinite(measuredTime)) return [];
  return rows
    .filter((row) => {
      const startTime = Date.parse(row.start_time);
      const endTime = row.end_time ? Date.parse(row.end_time) : Number.POSITIVE_INFINITY;
      return Number.isFinite(startTime) && measuredTime >= startTime && measuredTime <= endTime;
    })
    .map((row) => ({
      descriptive_name: row.descriptive_name,
      trial_type: row.trial_type
    }));
}

export function applyColorFilters(
  data: ColorMeasurement[],
  filters: ColorFilters,
  searchTerm: string
): ColorMeasurement[] {
  const search = searchTerm.trim();
  const loweredSearch = search.toLowerCase();
  const searchIsDateLike = isDateLikeSearchTerm(search);
  return data.filter((row) => {
    if (search) {
      if (searchIsDateLike) {
        if (!matchesDateSearch(row.measured_at, search)) return false;
      } else {
        const haystack = [
          row.measurement_label,
          row.method_name,
          row.observer_code,
          row.illuminant_code,
          row.glass_property_name,
          row.glass_property_code,
          row.glass_color,
          row.measurement_label,
          row.source_csv_path
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(loweredSearch)) return false;
      }
    }

    if (filters.date && !matchesDateFilter(row.measured_at, filters.date)) {
      return false;
    }
    if (filters.methodIds.length && !filters.methodIds.includes(row.measurement_method_id)) {
      return false;
    }
    if (filters.observerIds.length && !filters.observerIds.includes(row.applied_observer_id)) {
      return false;
    }
    if (filters.illuminantIds.length && !filters.illuminantIds.includes(row.applied_illuminant_id)) {
      return false;
    }
    if (filters.glassPropertyIds.length && !filters.glassPropertyIds.includes(row.glass_property_id)) {
      return false;
    }
    const selectedGlassColors = filters.glassColors || [];
    if (selectedGlassColors.length && !selectedGlassColors.includes(row.glass_color || "")) {
      return false;
    }
    if (!matchesTrialFilters(row.active_trials || [], filters.trials)) {
      return false;
    }
    return true;
  });
}

export function sortColorMeasurements(
  rows: ColorMeasurement[],
  sort: ColorSortState
): ColorMeasurement[] {
  const direction = sort.direction === "asc" ? 1 : -1;
  const next = [...rows];
  next.sort((left, right) => {
    if (sort.field === "measured_at") {
      return compareNullable(left.measured_at, right.measured_at) * direction;
    }
    if (sort.field === "measurement") {
      return compareNullable(
        left.measurement_label || left.source_csv_path || left.id,
        right.measurement_label || right.source_csv_path || right.id
      ) * direction;
    }
    if (sort.field === "method") {
      return compareNullable(left.method_name, right.method_name) * direction;
    }
    if (sort.field === "glass_color") {
      return compareNullable(left.glass_color, right.glass_color) * direction;
    }
    if (sort.field === "thickness_mean_mm") {
      return compareNullable(left.thickness_mean_mm, right.thickness_mean_mm) * direction;
    }
    if (sort.field === "brightness_percent") {
      return compareNullable(left.brightness_percent, right.brightness_percent) * direction;
    }
    if (sort.field === "fe2_fe_total_percent") {
      return compareNullable(left.fe2_fe_total_percent, right.fe2_fe_total_percent) * direction;
    }
    if (sort.field === "fe2_fe3_ratio") {
      return compareNullable(left.fe2_fe3_ratio, right.fe2_fe3_ratio) * direction;
    }
    if (sort.field === "dominant_wavelength_nm") {
      return compareNullable(left.dominant_wavelength_nm, right.dominant_wavelength_nm) * direction;
    }
    return (
      compareNullable(left.x_chromaticity, right.x_chromaticity) * direction ||
      compareNullable(left.y_chromaticity, right.y_chromaticity) * direction
    );
  });
  return next;
}

export function getCurvePoints(
  detail: ColorMeasurementDetail | null | undefined,
  curveKind: ColorCurveKind
): ColorCurvePoint[] {
  return detail?.curves?.[curveKind] || [];
}

export function computeBeerLambertComparison(
  reference: ColorMeasurementDetail | null | undefined,
  target: ColorMeasurementDetail | null | undefined
): BeerLambertComparison | null {
  if (!reference || !target) return null;
  const aligned = alignCurvePoints(
    getCurvePoints(reference, "absorbance_per_mm"),
    getCurvePoints(target, "absorbance_per_mm")
  );
  if (aligned.length < 2) {
    return {
      measurementId: target.id,
      slope: null,
      intercept: null,
      r2: null,
      rmse: null,
      meanDeltaAbsorbancePerMm: null,
      maxDeltaAbsorbancePerMm: null,
      integratedDeltaAbsorbancePerMm: null,
      pointCount: aligned.length
    };
  }

  const xs = aligned.map((row) => row.reference);
  const ys = aligned.map((row) => row.target);
  const meanX = average(xs);
  const meanY = average(ys);

  let numerator = 0;
  let denominator = 0;
  xs.forEach((value, index) => {
    const centeredX = value - meanX;
    numerator += centeredX * (ys[index] - meanY);
    denominator += centeredX * centeredX;
  });
  const slope = denominator > 0 ? numerator / denominator : null;
  const intercept = slope == null ? null : meanY - slope * meanX;

  let rss = 0;
  let tss = 0;
  let maxDelta = Number.NEGATIVE_INFINITY;
  let deltaSum = 0;
  let integratedDelta = 0;
  aligned.forEach((row) => {
    const fitted = slope == null || intercept == null ? meanY : slope * row.reference + intercept;
    const residual = row.target - fitted;
    rss += residual * residual;
    const centered = row.target - meanY;
    tss += centered * centered;
    const delta = row.target - row.reference;
    deltaSum += delta;
    if (delta > maxDelta) maxDelta = delta;
    integratedDelta += delta * row.step;
  });

  return {
    measurementId: target.id,
    slope,
    intercept,
    r2: tss > 0 ? 1 - rss / tss : null,
    rmse: Math.sqrt(rss / aligned.length),
    meanDeltaAbsorbancePerMm: deltaSum / aligned.length,
    maxDeltaAbsorbancePerMm: Number.isFinite(maxDelta) ? maxDelta : null,
    integratedDeltaAbsorbancePerMm: integratedDelta,
    pointCount: aligned.length
  };
}

export function renderLineChartSvg(
  title: string,
  series: SvgSeries[],
  width = 900,
  height = 320
): string {
  const activeSeries = series.filter((row) => row.points.length);
  if (!activeSeries.length) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><rect width="100%" height="100%" fill="#f5f7fb"/><text x="${width / 2}" y="${height / 2}" text-anchor="middle" fill="#6b7280" font-family="Arial" font-size="14">No curve data</text></svg>`;
  }

  const margin = { top: 34, right: 18, bottom: 28, left: 46 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  const allPoints = activeSeries.flatMap((row) => row.points);
  const xMin = Math.min(...allPoints.map((row) => row.x));
  const xMax = Math.max(...allPoints.map((row) => row.x));
  const yMinRaw = Math.min(...allPoints.map((row) => row.y));
  const yMaxRaw = Math.max(...allPoints.map((row) => row.y));
  const yPad = yMaxRaw === yMinRaw ? Math.max(0.1, Math.abs(yMaxRaw || 1) * 0.1) : (yMaxRaw - yMinRaw) * 0.1;
  const yMin = yMinRaw - yPad;
  const yMax = yMaxRaw + yPad;
  const scaleX = (value: number) =>
    margin.left + ((value - xMin) / Math.max(1e-9, xMax - xMin)) * innerWidth;
  const scaleY = (value: number) =>
    margin.top + innerHeight - ((value - yMin) / Math.max(1e-9, yMax - yMin)) * innerHeight;

  const gridLines = Array.from({ length: 5 }, (_, index) => {
    const fraction = index / 4;
    const yValue = yMin + (yMax - yMin) * fraction;
    const y = scaleY(yValue);
    return `<g>
      <line x1="${margin.left}" y1="${y}" x2="${width - margin.right}" y2="${y}" stroke="#d7deea" stroke-width="1"/>
      <text x="${margin.left - 8}" y="${y + 4}" text-anchor="end" fill="#64748b" font-family="Arial" font-size="11">${formatSvgNumber(yValue)}</text>
    </g>`;
  }).join("");

  const xLabels = Array.from({ length: 5 }, (_, index) => {
    const fraction = index / 4;
    const value = xMin + (xMax - xMin) * fraction;
    const x = scaleX(value);
    return `<g>
      <line x1="${x}" y1="${margin.top}" x2="${x}" y2="${height - margin.bottom}" stroke="#eef2f8" stroke-width="1"/>
      <text x="${x}" y="${height - 8}" text-anchor="middle" fill="#64748b" font-family="Arial" font-size="11">${formatSvgNumber(value)}</text>
    </g>`;
  }).join("");

  const curves = activeSeries
    .map((row) => {
      const path = row.points
        .map((point, index) => `${index === 0 ? "M" : "L"} ${scaleX(point.x).toFixed(2)} ${scaleY(point.y).toFixed(2)}`)
        .join(" ");
      return `<path d="${path}" fill="none" stroke="${row.color}" stroke-width="2.4" ${
        row.dashed ? 'stroke-dasharray="6 5"' : ""
      }/>`;
    })
    .join("");

  const legend = activeSeries
    .slice(0, 6)
    .map(
      (row, index) => `<g transform="translate(${margin.left + index * 138}, ${height - 4})">
        <line x1="0" y1="-3" x2="18" y2="-3" stroke="${row.color}" stroke-width="3" ${
          row.dashed ? 'stroke-dasharray="6 5"' : ""
        } />
        <text x="24" y="1" fill="#334155" font-family="Arial" font-size="11">${escapeHtml(row.label)}</text>
      </g>`
    )
    .join("");

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
    <rect width="100%" height="100%" fill="#ffffff"/>
    <text x="${margin.left}" y="22" fill="#0f172a" font-family="Arial" font-size="15" font-weight="700">${escapeHtml(title)}</text>
    ${gridLines}
    ${xLabels}
    <line x1="${margin.left}" y1="${height - margin.bottom}" x2="${width - margin.right}" y2="${height - margin.bottom}" stroke="#94a3b8" stroke-width="1.2"/>
    <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${height - margin.bottom}" stroke="#94a3b8" stroke-width="1.2"/>
    ${curves}
    ${legend}
  </svg>`;
}

export function renderChromaticitySvg(
  title: string,
  points: ChromaticityPoint[],
  highlightId?: string | null,
  width = 420,
  height = 360
): string {
  const margin = { top: 32, right: 20, bottom: 28, left: 38 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  const xMin = 0;
  const xMax = 0.8;
  const yMin = 0;
  const yMax = 0.9;
  const scaleX = (value: number) => margin.left + ((value - xMin) / (xMax - xMin)) * innerWidth;
  const scaleY = (value: number) => margin.top + innerHeight - ((value - yMin) / (yMax - yMin)) * innerHeight;
  const locusPath = [
    ...CHROMATICITY_LOCUS.map((row, index) => `${index === 0 ? "M" : "L"} ${scaleX(row.x).toFixed(2)} ${scaleY(row.y).toFixed(2)}`),
    `L ${scaleX(CHROMATICITY_LOCUS[0].x).toFixed(2)} ${scaleY(CHROMATICITY_LOCUS[0].y).toFixed(2)}`
  ].join(" ");

  const gridLabels = Array.from({ length: 5 }, (_, index) => index * 0.2);
  const grid = gridLabels
    .map((tick) => {
      const x = scaleX(tick);
      const y = scaleY(tick);
      return `<g>
        <line x1="${x}" y1="${margin.top}" x2="${x}" y2="${height - margin.bottom}" stroke="#eef2f8" stroke-width="1"/>
        <line x1="${margin.left}" y1="${y}" x2="${width - margin.right}" y2="${y}" stroke="#eef2f8" stroke-width="1"/>
        <text x="${x}" y="${height - 8}" text-anchor="middle" fill="#64748b" font-family="Arial" font-size="11">${tick.toFixed(1)}</text>
        <text x="${margin.left - 8}" y="${y + 4}" text-anchor="end" fill="#64748b" font-family="Arial" font-size="11">${tick.toFixed(1)}</text>
      </g>`;
    })
    .join("");

  const pointMarkup = points
    .filter((row) => Number.isFinite(row.x) && Number.isFinite(row.y))
    .map((row) => {
      const highlighted = highlightId != null && row.id === highlightId;
      const radius = highlighted ? 6 : 4;
      const stroke = highlighted ? "#0f172a" : "#ffffff";
      return `<g>
        <circle cx="${scaleX(row.x)}" cy="${scaleY(row.y)}" r="${radius}" fill="${row.color}" stroke="${stroke}" stroke-width="1.8"/>
        <text x="${scaleX(row.x) + 8}" y="${scaleY(row.y) - 8}" fill="#334155" font-family="Arial" font-size="11">${escapeHtml(row.label)}</text>
      </g>`;
    })
    .join("");

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
    <rect width="100%" height="100%" fill="#ffffff"/>
    <text x="${margin.left}" y="20" fill="#0f172a" font-family="Arial" font-size="15" font-weight="700">${escapeHtml(title)}</text>
    ${grid}
    <image
      href="${CHROMATICITY_BACKGROUND_IMAGE}"
      x="${scaleX(0)}"
      y="${scaleY(0.9)}"
      width="${scaleX(0.8) - scaleX(0)}"
      height="${scaleY(0) - scaleY(0.9)}"
      preserveAspectRatio="none"
    />
    <path d="${locusPath}" fill="rgba(148, 163, 184, 0.08)" stroke="#64748b" stroke-width="1.4"/>
    <line x1="${scaleX(CHROMATICITY_LOCUS[0].x)}" y1="${scaleY(CHROMATICITY_LOCUS[0].y)}" x2="${scaleX(CHROMATICITY_LOCUS[CHROMATICITY_LOCUS.length - 1].x)}" y2="${scaleY(CHROMATICITY_LOCUS[CHROMATICITY_LOCUS.length - 1].y)}" stroke="#94a3b8" stroke-width="1"/>
    ${pointMarkup}
    <line x1="${margin.left}" y1="${height - margin.bottom}" x2="${width - margin.right}" y2="${height - margin.bottom}" stroke="#94a3b8" stroke-width="1.2"/>
    <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${height - margin.bottom}" stroke="#94a3b8" stroke-width="1.2"/>
  </svg>`;
}

export function renderLabSvg(
  title: string,
  points: Array<{
    id: string;
    label: string;
    l: number;
    a: number;
    b: number;
    color: string;
  }>,
  highlightId?: string | null,
  width = 420,
  height = 360
): string {
  const margin = { top: 32, right: 20, bottom: 28, left: 38 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  const xMin = -160;
  const xMax = 160;
  const yMin = -130;
  const yMax = 130;
  const scaleX = (value: number) => margin.left + ((value - xMin) / (xMax - xMin)) * innerWidth;
  const scaleY = (value: number) => margin.top + innerHeight - ((value - yMin) / (yMax - yMin)) * innerHeight;

  const gridLabels = [-120, -60, 0, 60, 120];
  const grid = gridLabels
    .map((tick) => {
      const x = scaleX(tick);
      const y = scaleY(tick);
      return `<g>
        <line x1="${x}" y1="${margin.top}" x2="${x}" y2="${height - margin.bottom}" stroke="#d7deea" stroke-width="1"/>
        <line x1="${margin.left}" y1="${y}" x2="${width - margin.right}" y2="${y}" stroke="#d7deea" stroke-width="1"/>
        <text x="${x}" y="${height - 8}" text-anchor="middle" fill="#64748b" font-family="Arial" font-size="11">${tick}</text>
        <text x="${margin.left - 8}" y="${y + 4}" text-anchor="end" fill="#64748b" font-family="Arial" font-size="11">${tick}</text>
      </g>`;
    })
    .join("");

  const neutralPoint = `<circle cx="${scaleX(0)}" cy="${scaleY(0)}" r="4.5" fill="#2563eb" stroke="#ffffff" stroke-width="1.2"/>`;
  const pointMarkup = points
    .filter((row) => Number.isFinite(row.a) && Number.isFinite(row.b))
    .map((row) => {
      const highlighted = highlightId != null && row.id === highlightId;
      const radius = highlighted ? 7 : 5;
      const stroke = highlighted ? "#111827" : "rgba(15,23,42,0.45)";
      return `<g>
        <circle cx="${scaleX(row.a)}" cy="${scaleY(row.b)}" r="${radius}" fill="${row.color}" stroke="${stroke}" stroke-width="1.6"/>
        <text x="${scaleX(row.a) + 8}" y="${scaleY(row.b) - 8}" fill="#334155" font-family="Arial" font-size="11">${escapeHtml(row.label)}</text>
      </g>`;
    })
    .join("");

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
    <rect width="100%" height="100%" fill="#ffffff"/>
    <text x="${margin.left}" y="20" fill="#0f172a" font-family="Arial" font-size="15" font-weight="700">${escapeHtml(title)}</text>
    ${grid}
    <image
      href="${LAB_COLOR_PLANE_IMAGE}"
      x="${scaleX(-120)}"
      y="${scaleY(120)}"
      width="${scaleX(120) - scaleX(-120)}"
      height="${scaleY(-120) - scaleY(120)}"
      preserveAspectRatio="none"
    />
    <line x1="${scaleX(0)}" y1="${margin.top}" x2="${scaleX(0)}" y2="${height - margin.bottom}" stroke="#111827" stroke-width="1.5"/>
    <line x1="${margin.left}" y1="${scaleY(0)}" x2="${width - margin.right}" y2="${scaleY(0)}" stroke="#111827" stroke-width="1.5"/>
    ${neutralPoint}
    ${pointMarkup}
    <line x1="${margin.left}" y1="${height - margin.bottom}" x2="${width - margin.right}" y2="${height - margin.bottom}" stroke="#94a3b8" stroke-width="1.2"/>
    <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${height - margin.bottom}" stroke="#94a3b8" stroke-width="1.2"/>
  </svg>`;
}

function matchesTrialFilters(activeTrials: ActiveTrial[], selections: Record<TrialType, string[]>) {
  const selectionEntries = Object.entries(selections) as [TrialType, string[]][];
  for (const [trialType, selectedNames] of selectionEntries) {
    if (!selectedNames.length) continue;
    const matchesGroup = activeTrials.some(
      (trial) =>
        trial.trial_type.toLowerCase() === trialType.toLowerCase() &&
        selectedNames.includes(trial.descriptive_name)
    );
    if (!matchesGroup) return false;
  }
  return true;
}

function compareNullable(left: unknown, right: unknown) {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }
  return String(left).localeCompare(String(right));
}

function alignCurvePoints(
  reference: ColorCurvePoint[],
  target: ColorCurvePoint[]
): Array<{ wavelength_nm: number; reference: number; target: number; step: number }> {
  const targetMap = new Map<number, number>();
  target.forEach((row) => {
    targetMap.set(Number(row.wavelength_nm.toFixed(6)), row.value);
  });
  return reference
    .map((row, index) => {
      const wavelength = Number(row.wavelength_nm.toFixed(6));
      const targetValue = targetMap.get(wavelength);
      if (targetValue == null) return null;
      const previous = reference[Math.max(0, index - 1)]?.wavelength_nm ?? row.wavelength_nm;
      const next = reference[Math.min(reference.length - 1, index + 1)]?.wavelength_nm ?? row.wavelength_nm;
      const step = index === 0 ? next - row.wavelength_nm : row.wavelength_nm - previous;
      return {
        wavelength_nm: row.wavelength_nm,
        reference: row.value,
        target: targetValue,
        step: Math.abs(step) || 1
      };
    })
    .filter(
      (row): row is { wavelength_nm: number; reference: number; target: number; step: number } =>
        !!row
    );
}

function average(values: number[]) {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function formatSvgNumber(value: number) {
  if (Math.abs(value) >= 100) return value.toFixed(0);
  if (Math.abs(value) >= 10) return value.toFixed(1);
  return value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function measurementDisplayColor(
  measurement: ColorLikeMeasurement,
  fallback = "#225145"
) {
  if (
    Number.isFinite(measurement.l_star) &&
    Number.isFinite(measurement.a_star) &&
    Number.isFinite(measurement.b_star)
  ) {
    return labToDisplayCss(
      Number(measurement.l_star),
      Number(measurement.a_star),
      Number(measurement.b_star),
      true
    );
  }
  if (
    Number.isFinite(measurement.x_chromaticity) &&
    Number.isFinite(measurement.y_chromaticity)
  ) {
    return xyToChromaticityCss(
      Number(measurement.x_chromaticity),
      Number(measurement.y_chromaticity)
    );
  }
  return fallback;
}

function buildChromaticityBackgroundImage() {
  const width = 800;
  const height = 900;
  const cols = 80;
  const rows = 90;
  const xMax = 0.8;
  const yMax = 0.9;
  const polygon = CHROMATICITY_LOCUS.map((point) => ({ x: point.x, y: point.y }));
  const cellWidth = width / cols;
  const cellHeight = height / rows;

  const cells = Array.from({ length: rows }, (_, row) =>
    Array.from({ length: cols }, (_, col) => {
      const x = ((col + 0.5) / cols) * xMax;
      const y = yMax - ((row + 0.5) / rows) * yMax;
      if (!isPointInPolygon(x, y, polygon)) return "";
      return `<rect x="${(col * cellWidth).toFixed(2)}" y="${(row * cellHeight).toFixed(2)}" width="${cellWidth.toFixed(2)}" height="${cellHeight.toFixed(2)}" fill="${xyToChromaticityCss(x, y)}"/>`;
    }).join("")
  ).join("");

  return svgToDataUri(`
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <rect width="100%" height="100%" fill="transparent"/>
      ${cells}
    </svg>
  `);
}

function buildLabColorPlaneImage() {
  const width = 600;
  const height = 600;
  const cols = 88;
  const rows = 88;
  const radius = 120;
  const min = -radius;
  const max = radius;
  const lightness = 70;
  const cellWidth = width / cols;
  const cellHeight = height / rows;

  const cells = Array.from({ length: rows }, (_, row) =>
    Array.from({ length: cols }, (_, col) => {
      const a = min + ((col + 0.5) / cols) * (max - min);
      const b = max - ((row + 0.5) / rows) * (max - min);
      if (Math.hypot(a, b) > radius) return "";
      return `<rect x="${(col * cellWidth).toFixed(2)}" y="${(row * cellHeight).toFixed(2)}" width="${cellWidth.toFixed(2)}" height="${cellHeight.toFixed(2)}" fill="${labToDisplayCss(lightness, a, b, true)}"/>`;
    }).join("")
  ).join("");

  return svgToDataUri(`
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <defs>
        <clipPath id="lab-disc">
          <circle cx="${width / 2}" cy="${height / 2}" r="${width / 2}"/>
        </clipPath>
      </defs>
      <g clip-path="url(#lab-disc)">
        ${cells}
      </g>
      <circle
        cx="${width / 2}"
        cy="${height / 2}"
        r="${width / 2}"
        fill="none"
        stroke="rgba(15,23,42,0.18)"
        stroke-width="2"
      />
    </svg>
  `);
}

function xyToChromaticityCss(x: number, y: number, luminance = 1) {
  const xyz = xyToXyz(x, y, luminance);
  if (!xyz) return "#000000";
  return linearRgbToCss(xyzToLinearSrgb(...xyz), { normalizeToMax: true });
}

function xyToSrgbCss(x: number, y: number, luminance = 1) {
  const xyz = xyToXyz(x, y, luminance);
  if (!xyz) return "#000000";
  return xyzToSrgbCss(...xyz);
}

function xyToXyz(x: number, y: number, luminance = 1): [number, number, number] | null {
  if (!Number.isFinite(x) || !Number.isFinite(y) || y <= 0) return null;
  const X = (x * luminance) / y;
  const Y = luminance;
  const Z = ((1 - x - y) * luminance) / y;
  return [X, Y, Z];
}

function labToSrgbCss(l: number, a: number, b: number) {
  return labToDisplayCss(l, a, b);
}

function labToDisplayCss(l: number, a: number, b: number, compressOutOfGamut = false) {
  let scaledA = a;
  let scaledB = b;
  let linear = xyzToLinearSrgb(...labToXyz(l, scaledA, scaledB));

  if (compressOutOfGamut && !isLinearRgbInGamut(linear)) {
    let low = 0;
    let high = 1;
    for (let iteration = 0; iteration < 16; iteration += 1) {
      const scale = (low + high) / 2;
      const candidate = xyzToLinearSrgb(...labToXyz(l, a * scale, b * scale));
      if (isLinearRgbInGamut(candidate)) {
        low = scale;
        linear = candidate;
      } else {
        high = scale;
      }
    }
    scaledA = a * low;
    scaledB = b * low;
    linear = xyzToLinearSrgb(...labToXyz(l, scaledA, scaledB));
  }

  return linearRgbToCss(linear);
}

function labToXyz(l: number, a: number, b: number): [number, number, number] {
  const epsilon = 216 / 24389;
  const kappa = 24389 / 27;
  const white = { x: 0.95047, y: 1, z: 1.08883 };

  const fy = (l + 16) / 116;
  const fx = fy + a / 500;
  const fz = fy - b / 200;

  const xr = Math.pow(fx, 3) > epsilon ? Math.pow(fx, 3) : (116 * fx - 16) / kappa;
  const yr = l > kappa * epsilon ? Math.pow((l + 16) / 116, 3) : l / kappa;
  const zr = Math.pow(fz, 3) > epsilon ? Math.pow(fz, 3) : (116 * fz - 16) / kappa;

  return [xr * white.x, yr * white.y, zr * white.z];
}

function xyzToSrgbCss(x: number, y: number, z: number) {
  return linearRgbToCss(xyzToLinearSrgb(x, y, z));
}

function xyzToLinearSrgb(x: number, y: number, z: number): [number, number, number] {
  return [
    3.2406 * x - 1.5372 * y - 0.4986 * z,
    -0.9689 * x + 1.8758 * y + 0.0415 * z,
    0.0557 * x - 0.204 * y + 1.057 * z
  ];
}

function linearRgbToCss(
  [red, green, blue]: [number, number, number],
  options?: { normalizeToMax?: boolean }
) {
  const normalizeToMax = options?.normalizeToMax ?? false;
  let values = [red, green, blue].map((value) => (Number.isFinite(value) ? Math.max(0, value) : 0));

  if (normalizeToMax) {
    const maxValue = Math.max(...values);
    if (maxValue > 0) {
      values = values.map((value) => value / maxValue);
    }
  }

  return `rgb(${values.map(linearToSrgb).join(", ")})`;
}

function isLinearRgbInGamut([red, green, blue]: [number, number, number]) {
  return [red, green, blue].every(
    (value) => Number.isFinite(value) && value >= 0 && value <= 1
  );
}

function linearToSrgb(value: number) {
  const clamped = clamp01(value);
  const gamma =
    clamped <= 0.0031308
      ? 12.92 * clamped
      : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
  return Math.round(clamp01(gamma) * 255);
}

function clamp01(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function isPointInPolygon(x: number, y: number, polygon: Array<{ x: number; y: number }>) {
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const xi = polygon[index].x;
    const yi = polygon[index].y;
    const xj = polygon[previous].x;
    const yj = polygon[previous].y;
    const intersects =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-9) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function svgToDataUri(svg: string) {
  const compact = svg.replace(/\s{2,}/g, " ").trim();
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(compact)}`;
}
